import { EmailVerifyChip } from "@/components/EmailVerifyChip";
import React, { useEffect, useState } from "react";
import LocationPinPicker from "@/components/maps/LocationPinPicker";
import i18n, { T } from "@/i18n";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { getProfile, updateProfile, myFollows, myLikes, myComments, fetchMyActivities, fetchMyShops } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User as UserIcon, Store, Package, Wrench, BarChart3, Heart, Edit3, MapPin, Phone, Save, Camera, Navigation, X, LogOut, Users, MessageSquare, ExternalLink, Briefcase } from "lucide-react";
import ShopStatusPill from "@/components/ShopStatusPill";
import ImageInput from "@/components/ImageInput";
import AvatarPhotoEditor from "@/components/AvatarPhotoEditor";
import PrivacySettings from "@/components/profile/PrivacySettings";
import { resolveImageUrl } from "@/lib/api";

const SUGGESTED_CATEGORIES = ["Restaurant", "Cafe", "Bakery", "General Store", "Electronics", "Mobile Store", "Boutique", "Salon", "Pharmacy", "Furniture", "Hardware", "Coaching", "Service Center", "Grocery"];

export default function ProfilePage() {
  const { user, authChecking, refreshUser, formatApiError, logout, isVendor, mode, setMode } = useApp();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("setup") !== "1") return;
    setEditing(true);
    searchParams.delete("setup");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const [tab, setTab] = useState("overview");
  const [follows, setFollows] = useState([]);
  const [likes, setLikes] = useState([]);
  const [comments, setComments] = useState([]);
  const [sideLoading, setSideLoading] = useState(false);
  const [pro, setPro] = useState(null);

  const load = () => {
    setLoading(true);
    getProfile().then(setProfile).catch(() => {}).finally(() => setLoading(false));
    fetchMyActivities().then((a) => {
      const prof = a?.profiles?.professional || {};
      const l = a?.professional_listing;
      if (!a?.activities?.includes("professional") && !l) return setPro(null);
      const live = !!(l?.active && l?.review_status === "approved");
      setPro({ name: prof.name, profession: prof.profession, live, public: !!(l?.active || prof.published),
        status: l ? (l.review_status === "pending" ? "Pending approval" : l.active ? "Listed" : "Hidden") : "Not published" });
    }).catch(() => setPro(null));
  };

  useEffect(() => {
    if (authChecking) return;
    if (!user) { navigate("/", { replace: true }); return; }
    load();
  }, [user, authChecking]);

  useEffect(() => {
    if (!user || tab === "overview") return;
    setSideLoading(true);
    const fetcher = tab === "following" ? myFollows() : tab === "liked" ? myLikes() : tab === "activity" ? myComments() : Promise.resolve([]);
    fetcher
      .then((data) => {
        if (tab === "following") setFollows(data);
        else if (tab === "liked") setLikes(data);
        else if (tab === "activity") setComments(data);
      })
      .catch(() => {})
      .finally(() => setSideLoading(false));
  }, [tab, user]);

  const doLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  const switchToVendor = () => {
    setMode("vendor");
    navigate("/vendor");
  };

  if (!user || loading || !profile) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-slate-500"><T k="p.loading" /></div>;
  }

  const { user: u, shop, is_vendor, role_label, products_count } = profile;
  const initials = u.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10" data-testid="profile-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-4"><ArrowLeft className="w-4 h-4 rtl-flip" /> <T k="p.backHome" /></Link>
      <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-2" data-testid="profile-page-title">{i18n.language?.startsWith("hi") ? "मेरा खाता / My Account" : "My Account / मेरा खाता"}</div>

      {/* My Profile */}
      <section className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6 mb-4" data-testid="profile-header-section">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-display font-bold text-2xl overflow-hidden flex-shrink-0" data-testid="profile-avatar">
            {u.avatar_url ? <img src={resolveImageUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" /> : initials}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 truncate" data-testid="profile-name">{u.name}</h1>
            <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-full" data-testid="role-label">{role_label}</span>
          </div>
          <button onClick={() => setEditing(true)} className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 px-3 sm:px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="edit-profile-btn">
            <Edit3 className="w-4 h-4" /> <span className="hidden sm:inline"><T k="p.editProfile" /></span><span className="sm:hidden">Edit</span>
          </button>
        </div>
      </section>

      {/* Personal Information */}
      <section className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6 mb-4" data-testid="personal-info-section">
        <SectionTitle title="Personal Information" />
        <dl className="divide-y divide-slate-100">
          <InfoRow label={<T k="auth.fullName" />} value={u.name} testid="info-name" />
          <InfoRow label={<T k="auth.email" />} value={u.email} testid="info-email" />
          <InfoRow label={<T k="p.phone" />} value={u.phone} testid="info-phone" />
          <InfoRow label={<T k="p.address" />} value={u.address} testid="info-address" />
          <InfoRow label={<T k="p.pincode" />} value={u.pincode} testid="info-pincode" />
        </dl>
      </section>

      {/* Business Profile */}
      <section className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6 mb-4" data-testid="business-section">
        <SectionTitle title={<T k="p.businessProfile" />} />
        {shop ? (
          <div data-testid="business-profile-card">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-xl font-bold text-slate-900 min-w-0 break-words" data-testid="business-name">{shop.name}</h2>
              <ShopStatusPill status={shop.status} />
            </div>
            {shop.categories?.length > 0 && <div className="text-sm text-slate-600 mt-1"><T k="p.category" />: <span className="font-semibold" data-testid="business-category">{shop.categories.join(", ")}</span></div>}
            {shop.description && <p className="text-sm text-slate-600 mt-1 max-w-xl">{shop.description}</p>}
            {shop.address && <div className="text-xs text-slate-500 mt-1 inline-flex items-start gap-1"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {shop.address}, PIN {shop.pincode}</div>}
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 mt-4">
              <Link to={`/v/${shop.slug || shop.id}`} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50 px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="view-public-profile-link">
                <ExternalLink className="w-4 h-4" /> <T k="p.publicProfile" />
              </Link>
              <button onClick={switchToVendor} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="switch-to-vendor-btn">
                <Store className="w-4 h-4" /> <T k="nav.vendorDashboard" />
              </button>
            </div>
          </div>
        ) : pro ? (
          <div data-testid="professional-business-card">
            <div className="flex items-center gap-2 flex-wrap">
              <Briefcase className="w-5 h-5 text-emerald-700" />
              <h2 className="font-display text-xl font-bold text-slate-900" data-testid="professional-business-name">{pro.name || u.name}</h2>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${pro.live ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid="professional-listing-badge">{pro.live ? "Listing live" : pro.status}</span>
            </div>
            {pro.profession && <div className="text-sm text-slate-600 mt-1">{pro.profession}</div>}
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 mt-4">
              {pro.public && <Link to={`/p/${u.id}`} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50 px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="pro-view-public-profile-link"><ExternalLink className="w-4 h-4" /> <T k="p.publicProfile" /></Link>}
              <Link to="/onboarding" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="pro-manage-listing-link"><Edit3 className="w-4 h-4" /> Manage listing</Link>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 flex-wrap rounded-xl bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-100 p-4" data-testid="create-business-card">
            <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0"><Store className="w-5 h-5" /></div>
            <div className="flex-1 min-w-[10rem]">
              <h2 className="font-display text-lg font-bold text-slate-900"><T k="p.createBusiness" /></h2>
              <p className="text-sm text-slate-600 mt-1"><T k="p.createBusinessSub" /></p>
            </div>
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="create-business-btn">
              <Store className="w-4 h-4" /> <T k="p.setupBusiness" />
            </button>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap" data-testid="my-activities-card">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900"><T k="ob.myActivities" /></div>
            <div className="text-xs text-slate-500"><T k="ob.myActivitiesSub" /></div>
          </div>
          <button onClick={() => navigate("/onboarding")} className="rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50 px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="my-activities-btn"><T k="ob.setup" /></button>
        </div>
      </section>

      {/* Quick actions: vendors get all tiles, customers only Wishlist */}
      <div className={`grid gap-3 ${is_vendor ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:max-w-xs"}`}>
        {is_vendor && <QuickTile testid="tile-products" icon={Package} label={<T k="vendor.products" />} count={products_count} onClick={() => navigate("/vendor?tab=products")} />}
        {is_vendor && <QuickTile testid="tile-services" icon={Wrench} label={<T k="vendor.services" />} count={null} onClick={() => navigate("/vendor?tab=services")} />}
        {is_vendor && <QuickTile testid="tile-analytics" icon={BarChart3} label={<T k="vendor.analytics" />} count={null} onClick={() => navigate("/vendor?tab=analytics")} />}
        <QuickTile testid="tile-wishlist" icon={Heart} label={<T k="nav.wishlist" />} count={null} onClick={() => document.querySelector("[data-testid='wishlist-btn']")?.click()} />
      </div>

      {/* Tabs for Liked / Following / Activity */}
      <div className="mt-6" data-testid="profile-tabs">
        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar border-b border-slate-100 mb-4">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="profile-tab-overview"><T k="p.overview" /></TabBtn>
          <TabBtn active={tab === "liked"} onClick={() => setTab("liked")} testid="profile-tab-liked"><Heart className="w-3.5 h-3.5" /> <T k="p.liked" /></TabBtn>
          <TabBtn active={tab === "following"} onClick={() => setTab("following")} testid="profile-tab-following"><Users className="w-3.5 h-3.5" /> <T k="p.following" /></TabBtn>
          <TabBtn active={tab === "activity"} onClick={() => setTab("activity")} testid="profile-tab-activity"><MessageSquare className="w-3.5 h-3.5" /> <T k="p.activity" /></TabBtn>
        </div>

        {tab === "overview" && (
          <div className="text-sm text-slate-500" data-testid="profile-overview-tab">
            <T k="p.welcomeOverview" />
          </div>
        )}
        {tab === "liked" && (
          <LikedProducts items={likes} loading={sideLoading} />
        )}
        {tab === "following" && (
          <FollowingList items={follows} loading={sideLoading} />
        )}
        {tab === "activity" && (
          <ActivityList items={comments} loading={sideLoading} />
        )}
      </div>

      <PrivacySettings />

      {/* Account & Security */}
      <section className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6 mt-8" data-testid="account-security-section">
        <SectionTitle title="Account & Security" />
        <dl className="divide-y divide-slate-100">
          <div className="py-2.5 grid grid-cols-[minmax(0,7rem)_1fr] sm:grid-cols-[10rem_1fr] gap-3 items-center" data-testid="info-email-verification">
            <dt className="text-xs font-semibold text-slate-500">Email verification</dt>
            <dd className="min-w-0"><EmailVerifyChip /></dd>
          </div>
        </dl>
        <div className="mt-4 flex justify-end">
          <button onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-full border border-red-100 text-red-700 hover:bg-red-50 px-4 py-2 min-h-[40px] text-sm font-semibold" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </section>

      <EditProfileDialog
        open={editing}
        onClose={() => setEditing(false)}
        profile={profile}
        onSaved={async () => { setEditing(false); await refreshUser(); load(); }}
        onAvatarSaved={() => getProfile().then(setProfile).catch(() => {})}
        formatApiError={formatApiError}
      />
    </div>
  );
}

function SectionTitle({ title }) {
  return <h2 className="font-display text-lg font-bold text-slate-900 mb-3">{title}</h2>;
}

function InfoRow({ label, value, testid }) {
  return (
    <div className="py-2.5 grid grid-cols-[minmax(0,7rem)_1fr] sm:grid-cols-[10rem_1fr] gap-3 items-start" data-testid={testid}>
      <dt className="text-xs font-semibold text-slate-500 pt-0.5">{label}</dt>
      <dd className={`text-sm min-w-0 break-words ${value ? "text-slate-900 font-medium" : "text-slate-400 italic"}`} data-testid={`${testid}-value`}>{value || "Not added"}</dd>
    </div>
  );
}

function QuickTile({ icon: Icon, label, count, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`h-full flex flex-col items-start justify-start text-left bg-white border border-slate-100 rounded-2xl p-4 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-emerald-400 hover:shadow-sm"}`}
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-display text-lg font-bold text-slate-900">{label}</div>
      {count !== null && count !== undefined && <div className="text-xs text-slate-500 mt-0.5">{count} total</div>}
    </button>
  );
}

function EditProfileDialog({ open, onClose, profile, onSaved, onAvatarSaved, formatApiError }) {
  const u = profile.user;
  const s = profile.shop;
  const shopCats = s?.categories;
  const [form, setForm] = useState({
    name: u.name || "", phone: u.phone || "", address: u.address || "", pincode: u.pincode || "", avatar_url: u.avatar_url || "",
    business_name: s?.name || "", business_category: s?.categories?.[0] || "",
    business_description: s?.description || "", shop_address: s?.address || "", shop_image: s?.image || "", shop_logo: s?.logo || "",
    latitude: s?.latitude ?? "", longitude: s?.longitude ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState(s?.shop_id || s?.id || "");
  useEffect(() => { if (open && s) fetchMyShops().then((l) => setShops(l || [])).catch(() => setShops([])); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickShop = (id) => {
    const x = shops.find((y) => y.shop_id === id);
    if (!x) return;
    setShopId(id);
    setForm((f) => ({ ...f, business_name: x.name || "", business_category: x.categories?.[0] || "", business_description: x.description || "", shop_address: x.address || "" }));
  };
  // Unchanged saved numbers are left alone; new input must be a valid 10-digit mobile.
  const phoneError = form.phone && form.phone !== (u.phone || "") && !/^[6-9]\d{9}$/.test(form.phone)
    ? "Valid 10-digit mobile number daalein (6, 7, 8 ya 9 se shuru)." : "";

  const submit = async (e) => {
    e.preventDefault();
    if (phoneError) { toast.error(phoneError); return; }
    setSaving(true);
    try {
      const patch = { ...form };
      patch.latitude = form.latitude === "" ? null : parseFloat(form.latitude);
      patch.longitude = form.longitude === "" ? null : parseFloat(form.longitude);
      // Drop null-lat/lng if empty original (avoid overwriting with null)
      if (patch.latitude === null) delete patch.latitude;
      if (patch.longitude === null) delete patch.longitude;
      if (s) {
        // Existing shop: only the text business fields shown here; images/PIN/map stay in Vendor Dashboard.
        ["shop_image", "shop_logo", "latitude", "longitude"].forEach((k) => delete patch[k]);
        patch.shop_id = shopId;
      }
      await updateProfile(patch);
      toast.success(i18n.t("p.saved"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-6 bg-slate-900/50 backdrop-blur-sm overflow-y-auto" data-testid="edit-profile-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg my-4 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold"><T k="p.editProfile" /></h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="close-edit"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 overflow-y-auto">
          <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider"><T k="p.personal" /></div>
          <Field label={<T k="auth.fullName" />} value={form.name} onChange={(v) => set("name", v)} testid="edit-name" required />
          <div>
            <Field label={<T k="p.phone" />} value={form.phone} onChange={(v) => set("phone", normalizePhone(v))} testid="edit-phone" inputMode="numeric" />
            {phoneError && <p className="text-[11px] font-semibold text-rose-600 mt-1" data-testid="edit-phone-error">{phoneError}</p>}
          </div>
          <Field label={<T k="p.address" />} value={form.address} onChange={(v) => set("address", v)} testid="edit-address" textarea />
          <Field label={<T k="p.pincode" />} value={form.pincode} onChange={(v) => set("pincode", v.replace(/\D/g, "").slice(0, 6))} testid="edit-pincode" />
          <AvatarPhotoEditor value={form.avatar_url} onSaved={(url) => { set("avatar_url", url); onAvatarSaved?.(); }} />
          {form.avatar_url && <button type="button" onClick={() => set("avatar_url", "")} className="text-[11px] font-semibold text-rose-600" data-testid="edit-avatar-remove">Remove photo (applies when you tap Save below)</button>}

          {s ? (
            <div className="pt-3 border-t border-slate-100" data-testid="edit-shop-moved">
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Shop details</div>
              {shops.length > 1 && (
                <label className="block mt-2">
                  <span className="text-xs font-semibold text-slate-600">Kaun si shop update karein?</span>
                  <select value={shopId} onChange={(e) => pickShop(e.target.value)} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white outline-none focus:border-emerald-500" data-testid="edit-shop-picker">
                    {shops.map((x) => <option key={x.shop_id} value={x.shop_id}>{x.name}{x.listed === false ? " (unlisted)" : ""}</option>)}
                  </select>
                </label>
              )}
              <div className="mt-2 space-y-3">
                <Field label={<T k="p.businessName" />} value={form.business_name} onChange={(v) => set("business_name", v)} testid="edit-business-name" />
                <Field label="Business category" value={form.business_category} onChange={(v) => set("business_category", v)} testid="edit-business-category" />
                <Field label="Business description" value={form.business_description} onChange={(v) => set("business_description", v)} testid="edit-business-description" textarea />
                <Field label="Shop address" value={form.shop_address} onChange={(v) => set("shop_address", v)} testid="edit-shop-address" textarea />
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Shop PIN, images, logo and map location: Vendor Dashboard → Shop Profile.</p>
              <Link to="/vendor?tab=shop" onClick={onClose} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-emerald-200 text-emerald-800 hover:bg-emerald-50 rounded-xl py-2 text-xs font-semibold" data-testid="edit-shop-in-dashboard">
                <Store className="w-3.5 h-3.5" /> Open Dashboard → Shop Profile
              </Link>
            </div>
          ) : (
            <>
          <div className="pt-3 border-t border-slate-100 text-xs uppercase font-semibold text-slate-500 tracking-wider"><T k="p.businessOptional" /></div>
          <p className="text-[11px] text-slate-500">Fill any of these fields to become a Vendor — no approval needed. You can also skip this and just upload a product later.</p>
          <Field label={<T k="p.businessName" />} value={form.business_name} onChange={(v) => set("business_name", v)} testid="edit-business-name" />
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Business category (free text)</label>
            <input list="cat-suggestions" value={form.business_category} onChange={(e) => set("business_category", e.target.value)} placeholder="e.g. Restaurant, Bakery, Boutique — anything" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="edit-business-category" />
            <datalist id="cat-suggestions">
              {SUGGESTED_CATEGORIES.map((c) => <option key={c} value={c} />)}
            </datalist>
            <p className="text-[10px] text-slate-400 mt-1">Suggestions are optional — enter anything.</p>
          </div>
          <Field label="Business description" value={form.business_description} onChange={(v) => set("business_description", v)} testid="edit-business-description" textarea />
          <Field label="Shop address" value={form.shop_address} onChange={(v) => set("shop_address", v)} testid="edit-shop-address" textarea />
          <ImageInput value={form.shop_image} onChange={(v) => set("shop_image", v)} label="Shop image" testid="edit-shop-image" disabled={saving} libraryCategory="shops" librarySuggest={shopCats} />
          <p className="text-[11px] text-slate-500 -mt-1">Gallery, Camera or Upload File (JPG, PNG, WEBP). Image link is optional. Tap Save changes after upload.</p>
          <ImageInput value={form.shop_logo} onChange={(v) => set("shop_logo", v)} label="Shop logo (square)" testid="edit-shop-logo" disabled={saving} />
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Shop location (map pin)</div>
            <LocationPinPicker lat={form.latitude} lng={form.longitude} onChange={(a, b) => { set("latitude", a); set("longitude", b); }} testid="edit-pin-picker" />
          </div>
            </>
          )}

          <button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl py-3 text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="save-profile-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Digits only; drops a pasted +91 / 0 prefix so "+91 98765-43210" becomes "9876543210".
function normalizePhone(v) {
  let d = String(v || "").replace(/\D/g, "");
  if (d.length > 10 && d.startsWith("91")) d = d.slice(2);
  else if (d.length > 10 && d.startsWith("0")) d = d.slice(1);
  return d.slice(0, 10);
}

function Field({ label, value, onChange, testid, textarea, required, inputMode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid={testid} required={required} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} inputMode={inputMode} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid={testid} required={required} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${active ? "border-emerald-700 text-emerald-800" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={testid}>
      {children}
    </button>
  );
}

function LikedProducts({ items, loading }) {
  if (loading) return <div className="py-6 text-center text-slate-500 text-sm"><T k="p.loading" /></div>;
  if (!items?.length) return <EmptyState icon={Heart} title="No liked products yet" hint="Products you like will appear here." testid="liked-empty" />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="liked-products-grid">
      {items.map((p) => (
        <Link key={p.id} to={`/product/${p.id}`} className="flex items-center gap-2 bg-white border border-slate-100 rounded-xl p-2 hover:border-emerald-400" data-testid={`liked-${p.id}`}>
          <img src={p.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
            <div className="text-xs text-slate-500 truncate">{p.shop_name}</div>
            <div className="text-xs font-bold text-emerald-800 mt-0.5">₹{p.price}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function FollowingList({ items, loading }) {
  if (loading) return <div className="py-6 text-center text-slate-500 text-sm"><T k="p.loading" /></div>;
  if (!items?.length) return <EmptyState icon={Users} title="Not following any vendor yet" hint="Follow shops to keep track of their new products and offers." testid="following-empty" />;
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="following-grid">
      {items.map((v) => (
        <Link key={v.id} to={`/v/${v.slug || v.id}`} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-3 hover:border-emerald-400" data-testid={`following-${v.id}`}>
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-emerald-50 flex-shrink-0">
            {v.image ? <img src={v.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-emerald-800">{v.name?.slice(0, 1)}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold truncate">{v.name}</div>
              <ShopStatusPill status={v.status} size="xs" />
            </div>
            <div className="text-xs text-slate-500 truncate">{v.categories?.join(", ") || "Local shop"}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function ActivityList({ items, loading }) {
  if (loading) return <div className="py-6 text-center text-slate-500 text-sm"><T k="p.loading" /></div>;
  if (!items?.length) return <EmptyState icon={MessageSquare} title="No activity yet" hint="Your comments on products will appear here." testid="activity-empty" />;
  return (
    <div className="space-y-2" data-testid="activity-list">
      {items.map((c) => (
        <div key={`${c.product_id}-${c.created_at}`} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-3">
          {c.product?.image && <img src={c.product.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            {c.product?.name && (
              <Link to={`/product/${c.product.id}`} className="text-xs font-semibold text-emerald-800 hover:underline">{c.product.name}</Link>
            )}
            <p className="text-sm text-slate-700 mt-0.5">{c.comment}</p>
            <div className="text-[10px] text-slate-400 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div className="py-10 text-center bg-white border border-slate-100 rounded-2xl" data-testid={testid}>
      <Icon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <div className="font-display text-lg font-bold text-slate-900">{title}</div>
      <p className="text-sm text-slate-500 mt-1">{hint}</p>
    </div>
  );
}
